import React, { useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

export default function RunsChart(props) {

  useEffect(() => {

    function convertDurationToMinutes(duration) {
      if (!duration) {
        return 0;
      }

      const parts = duration.split(':');
      if (parts.length === 3) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parseInt(parts[2], 10);
        return (hours * 60) + minutes + (seconds / 60);
      }
      else if (parts.length === 2) {
        const hours = 0;
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        return (hours * 60) + minutes + (seconds / 60);
      }
      else
        return (parts[0] / 60);
    }

    function getMaxDurationInMinutes(data) {
      return data
        .map(item => ({
          closeDuration: convertDurationToMinutes(item.closeDuration),
          salesDuration: convertDurationToMinutes(item.salesDuration),
        }))
        .reduce((max, item) => {
          const maxItemDuration = Math.max(item.closeDuration, item.salesDuration);
          return Math.max(max, maxItemDuration);
        }, 0);
    }

    function extractAndTransform(data) {
      return data
        .sort((a, b) => b.run - a.run)
        .map(item => ({
          loadFactor: item.loadFactor,
          arrivalVelocity: item.arrivalVelocity,
          closeDuration: convertDurationToMinutes(item.closeDuration),
          salesDuration: convertDurationToMinutes(item.salesDuration),
          gasVolumeForecast: item.gasVolumeForecast,
          run: item.run
        }));
    }

    //const trendsToBePlotted=[{"trend":"loadFactor"},"arrivalVelocity","closeDuration","salesDuration","gasVolumeForecast"];
    const trendsToBePlotted =
      [{ "trend": "loadFactor", "hexCode": "#FFD700", "displayText": "Load Factor" }, { "trend": "arrivalVelocity", "hexCode": "#4A6FE3", "displayText": "Arrival Velocity" }
        , { "trend": "closeDuration", "hexCode": "#FF6F61", "displayText": "Close Duration" }, { "trend": "salesDuration", "hexCode": "#9B5DE5", "displayText": "Sales Duration" },
      { "trend": "gasVolumeForecast", "hexCode": "#8ACB88", "displayText": "Projected Gas Volume" }
      ]

    // Create root element

    let data = extractAndTransform(props.trendData);
    let maxSalesAndCloseDurationLimit = getMaxDurationInMinutes(props.trendData)
    // debugger;
    am5.addLicense("AM5C137048159");
    let root = am5.Root.new("chartRuns");
    // root.numberFormatter.set("numberFormat", "#,###.00");
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = am5xy.XYChart.new(root, {
      focusable: true,
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      pinchZoomX: false
    });

    root.container.children.push(chart);
    chart.get("colors").set("step", 2);

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    // let xAxis = chart.xAxes.push(
    //   am5xy.ValueAxis.new(root, {
    //     strictMinMax: true,
    //     maxDeviation: 0,
    //     renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 50 }),
    //     tooltip: am5.Tooltip.new(root, {
    //       themeTags: ["axis"],
    //       animationDuration: 300
    //     })
    //   })
    // );
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "run",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
          fill: am5.color('#fff')
        })
      })
    );
    xAxis.get("renderer").grid.template.setAll({ stroke: am5.color(0xFFFFFF) });

    trendsToBePlotted.forEach(x => {
      let yRenderer = am5xy.AxisRendererY.new(root, {
        opposite: false
      })
      yRenderer.labels.template.set('visible', false);
      yRenderer.grid.template.set("strokeOpacity", 0);
      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          strictMinMax: true,
          // maxDeviation: 0.1,
          min: 0,
          extraMax: 0.01,

          renderer: yRenderer,

        })
      );

      if (x.trend === "arrivalVelocity")
        yAxis.set("max", 2 * props?.trendData?.[0]?.fastArrival);

      if (x.trend === "loadFactor")
        yAxis.set("max", 100)

      if (x.trend === "closeDuration" || x.trend === "salesDuration")
        yAxis.set("max", maxSalesAndCloseDurationLimit)

      if (chart.yAxes.indexOf(yAxis) > 0) {
        yAxis.set("syncWithAxis", chart.yAxes.getIndex(0) as any);
      }

      let series = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: x.trend,
          categoryXField: "run",
          // baseAxis: yAxis,
          name: x.displayText,
          legendLabelText: "[bold {stroke}]{name}:[/]",
          legendRangeLabelText: "[{stroke}]{name}:[/]",
          legendValueText: "[bold {stroke}]{valueY}[/]",
          legendRangeValueText: "[{stroke}]{valueYClose}[/]",
          stroke: am5.color(x.hexCode),
          // tooltip: am5.Tooltip.new(root, {
          //   labelText: "Run:{categoryX}, {name}:{valueY}"
          // })
        })
      );

      series.bullets.push(function () {
        let shape;
      
        switch (x.trend) {
          case "salesDuration": // Asterisk
            shape = am5.Star.new(root, {
              spikes: 6,
              radius: 6,
              innerRadius: 2,
              fill: am5.color(x.hexCode),
              centerX: am5.percent(50),
              centerY: am5.percent(50)
            });
            break;
      
          case "arrivalVelocity": // Triangle
            shape = am5.Triangle.new(root, {
              width: 6,
              height: 6,
              rotation: 0,
              fill: am5.color(x.hexCode),
              centerX: am5.percent(50),
              centerY: am5.percent(50)
            });
            break;
      
          case "loadFactor": // Square  
            shape = am5.Rectangle.new(root, {
              width: 4,
              height: 4,
              fill: am5.color(x.hexCode),
              centerX: am5.percent(50),
              centerY: am5.percent(50)
            });
            break;
      
            case "gasVolumeForecast": // X
            shape = am5.Graphics.new(root, {
              draw: function (display) {
                const size = 6;
                display.moveTo(-size / 2, -size / 2);
                display.lineTo(size / 2, size / 2);
                display.moveTo(size / 2, -size / 2);
                display.lineTo(-size / 2, size / 2);
              },
              stroke: am5.color(x.hexCode),
              strokeWidth: 2,
              centerX: am5.percent(50),
              centerY: am5.percent(50)
            });
            break;      
          default: // close duration (keep rectangle)
            shape = am5.Rectangle.new(root, {
              height: 2.5,
              width: 5,
              fill: am5.color(x.hexCode),
              centerX: am5.percent(50),
              centerY: am5.percent(50),
            });
        }     
        return am5.Bullet.new(root, {
          sprite: shape
        });
      });

      series.data.setAll(data);
      series.appear(1000, 0);
    });
    xAxis.data.setAll(data);
    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    chart.set("cursor", am5xy.XYCursor.new(root, {
      // xAxis: xAxis,
      behavior: "none"
      // yAxis: yAxis,
      // snapToSeries: [ series1]
    }));

    chart.zoomOutButton.set("forceHidden", true);

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/

    var legend = chart.children.unshift(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(-7.5),
      })
    );

    legend.labels.template.setAll({
      fontSize: 12,
      fontWeight: "400",
      fill: am5.color("#FFFFFF")
    });

    legend.valueLabels.template.setAll({
      fontSize: 12,
      fontWeight: "100"
    });

    legend.data.setAll(chart.series.values);
    xAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));
    chart.appear(1000, 100);

    var tooltip = am5.Tooltip.new(root, {});

    chart.plotContainer.set("tooltipPosition", "pointer");
    chart.plotContainer.set("tooltipText", "a");
    chart.plotContainer.set("tooltip", tooltip);

    tooltip.label.adapters.add("text", function (text, target) {
      text = ""
      var i = 0;

      let show = true;
      chart.series.each(function (series) {
        var tooltipDataItem = series.get("tooltipDataItem");
        if (tooltipDataItem) {
          if (i !== 0) {
            text += "\n";
          }


          if (show) {

            text += "[/] [bold]" + tooltipDataItem.get("categoryX") + '[/] ' + "\n";
            show = false;
          }
          //text += '[' + series.get("stroke").toString() + ']●[/] [bold width:100px]' + series.get("name") + ':[/] ' + (tooltipDataItem.get("valueY") == null ? "" : tooltipDataItem.get("valueY").toFixed(2));
          const trendSymbolMap = {
            salesDuration: "✶",           // Star-like (✶ or ✳ or ❊)
            arrivalVelocity: "▲",        // Triangle
            loadFactor: "■",        // Square
            gasVolumeForecast: "✕",    // X shape
            closeDuration: "●"       // Rectangle 
          };
          
          const trendName = series.get("name");
          const trendKey = trendsToBePlotted.find(t => t.displayText === trendName)?.trend;
          const bulletSymbol = trendSymbolMap[trendKey] || "●";
          const value = tooltipDataItem.get("valueY") == null ? "" : tooltipDataItem.get("valueY").toFixed(2);

          text += `[${series.get("stroke").toString()}]${bulletSymbol}[/] [bold width:100px]${series.get("name")}: [/]${value}`;
          
        }
        i++;
      });
      return text
    });
    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 0.4
    });


    return () => {
      root.dispose();
      chart.dispose();
    };

  }, [props.trendData])
  return <div id="chartRuns" style={{ width: '100%', height: '100%', paddingTop: '10px' }}></div>;
}



